import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Sparkle, Coins } from "@phosphor-icons/react";

export default function CreateTask() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [depts, setDepts] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    title: "", description: "", category: "",
    department_id: user.department_id || "",
    required_skills: "",
    effort_hours: 8, complexity: "medium", priority: "medium",
    reward_points: 100, credit_cost: "", visibility: "public",
    deadline: "", reviewer_id: "",
  });
  const [aiBusy, setAiBusy] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/departments").then(({ data }) => setDepts(data));
    api.get("/users").then(({ data }) => setUsers(data.filter((u) => ["REVIEWER", "MANAGER", "ADMIN"].includes(u.role))));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const enrich = async () => {
    if (!form.title) return toast.error("Please enter a title first.");
    setAiBusy(true);
    try {
      const { data } = await api.post("/tasks/ai/enrich", {
        title: form.title, description: form.description,
      });
      setAiSuggestion(data);
      setForm((f) => ({
        ...f,
        required_skills: data.skill_tags?.join(", ") || f.required_skills,
        effort_hours: data.suggested_effort_hours || f.effort_hours,
        complexity: data.complexity || f.complexity,
        credit_cost: data.suggested_credit_cost || f.credit_cost,
      }));
      toast.success("AI suggestions applied. Review and edit before posting.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "AI enrichment failed");
    } finally {
      setAiBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category || null,
        department_id: form.department_id || null,
        required_skills: form.required_skills.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean),
        effort_hours: Number(form.effort_hours),
        complexity: form.complexity,
        priority: form.priority,
        reward_points: Number(form.reward_points),
        credit_cost: form.credit_cost ? Number(form.credit_cost) : null,
        visibility: form.visibility,
        deadline: form.deadline || null,
        reviewer_id: form.reviewer_id || null,
        submit_for_approval: true,
      };
      const { data } = await api.post("/tasks", payload);
      toast.success(data.status === "open" ? "Task posted" : "Task submitted — awaiting manager approval");
      navigate(`/tasks/${data.task_id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to create");
    } finally {
      setBusy(false);
    }
  };

  if (!["ADMIN", "MANAGER", "TASK_OWNER"].includes(user.role)) {
    return (
      <div className="p-8 max-w-2xl mx-auto" data-testid="create-task-denied">
        <h1 className="text-2xl font-semibold">Not authorized</h1>
        <p className="text-sm text-zinc-500 mt-2">
          Only Task Owners, Managers and Admins can post tasks. Ask your admin to upgrade your role.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-3xl mx-auto" data-testid="create-task-page">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Post a task</div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-bold mt-1">Create a new task</h1>
        <p className="text-sm text-zinc-500 mt-1">Describe the work — let AI suggest skills, effort and credits.</p>
      </div>

      <form onSubmit={submit} className="space-y-5" data-testid="create-task-form">
        <Field label="Title">
          <Input data-testid="task-title-input" value={form.title} onChange={set("title")} required className="rounded-sm" placeholder="e.g. Migrate billing service" />
        </Field>
        <Field label="Description">
          <Textarea data-testid="task-description-input" value={form.description} onChange={set("description")} rows={5} className="rounded-sm" required placeholder="Context, scope, success criteria…" />
        </Field>

        <div className="border border-dashed border-[#E1252A]/40 p-4 rounded-sm bg-[#E1252A]/5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Sparkle size={16} className="text-[#E1252A]" />
              <div>
                <div className="text-sm font-semibold">AI assistant</div>
                <div className="text-xs text-zinc-500">Auto-fill skill tags, effort & credit cost.</div>
              </div>
            </div>
            <Button type="button" onClick={enrich} disabled={aiBusy} variant="outline" className="rounded-sm" data-testid="ai-enrich-btn">
              {aiBusy ? "Thinking…" : "Enrich with AI"}
            </Button>
          </div>
          {aiSuggestion?.ai_summary && (
            <div className="mt-3 text-sm text-zinc-700 dark:text-zinc-300 border-l-2 border-[#E1252A] pl-3">
              {aiSuggestion.ai_summary}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Department">
            <Select value={form.department_id} onValueChange={(v) => setForm((f) => ({ ...f, department_id: v }))}>
              <SelectTrigger className="rounded-sm" data-testid="task-department-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {depts.map((d) => <SelectItem key={d.dept_id} value={d.dept_id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Reviewer (optional)">
            <Select value={form.reviewer_id} onValueChange={(v) => setForm((f) => ({ ...f, reviewer_id: v }))}>
              <SelectTrigger className="rounded-sm" data-testid="task-reviewer-select"><SelectValue placeholder="Select reviewer" /></SelectTrigger>
              <SelectContent>
                {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name} · {u.role}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Required skills (comma separated)">
            <Input data-testid="task-skills-input" value={form.required_skills} onChange={set("required_skills")} className="rounded-sm" placeholder="python, react, devops" />
          </Field>
          <Field label="Category">
            <Input data-testid="task-category-input" value={form.category} onChange={set("category")} className="rounded-sm" placeholder="Engineering" />
          </Field>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Field label="Effort (hours)">
            <Input data-testid="task-effort-input" type="number" min={1} value={form.effort_hours} onChange={set("effort_hours")} className="rounded-sm" />
          </Field>
          <Field label="Complexity">
            <Select value={form.complexity} onValueChange={(v) => setForm((f) => ({ ...f, complexity: v }))}>
              <SelectTrigger className="rounded-sm" data-testid="task-complexity-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Priority">
            <Select value={form.priority} onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}>
              <SelectTrigger className="rounded-sm" data-testid="task-priority-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Reward (impact pts)">
            <Input data-testid="task-reward-input" type="number" min={0} value={form.reward_points} onChange={set("reward_points")} className="rounded-sm" />
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Credit cost (auto if blank)">
            <div className="relative">
              <Coins size={14} className="absolute left-3 top-3 text-[#E1252A]" />
              <Input data-testid="task-credits-input" type="number" min={0} value={form.credit_cost} onChange={set("credit_cost")} className="rounded-sm pl-8" placeholder="auto" />
            </div>
          </Field>
          <Field label="Deadline">
            <Input data-testid="task-deadline-input" type="datetime-local" value={form.deadline} onChange={set("deadline")} className="rounded-sm" />
          </Field>
          <Field label="Visibility">
            <Select value={form.visibility} onValueChange={(v) => setForm((f) => ({ ...f, visibility: v }))}>
              <SelectTrigger className="rounded-sm" data-testid="task-visibility-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="public">Public (org-wide)</SelectItem>
                <SelectItem value="department">Department only</SelectItem>
                <SelectItem value="private">Private (invite)</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="text-xs text-zinc-500">
            Every new task is reviewed by your manager before it goes live on the marketplace.
          </div>
          <Button type="submit" disabled={busy} className="rounded-sm bg-foreground text-background hover:bg-zinc-800 dark:hover:bg-zinc-200" data-testid="task-submit-btn">
            {busy ? "Submitting…" : "Submit for approval"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">{label}</Label>
      {children}
    </div>
  );
}
